import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAnalytics } from '@/lib/analytics'

/**
 * Parse UTM parameters off the current URL and attach them to every subsequent
 * analytics event as base properties.
 *
 * Recruiter opens
 *   software.garrettjohnlaw.com/for/valon?utm_source=resume&utm_medium=pdf&utm_campaign=valon-sr-swe
 *
 * GA4 picks up the UTMs natively for page_view events, but we also want them
 * on custom events (resume_download, contact_click). This hook mirrors them
 * into the analytics provider's track() calls via an `identify` with UTM
 * traits — lighter-weight than hand-passing them on every call.
 *
 * Re-runs on route change so in-app navigation picks up new attribution.
 */
export function useUTMAttribution() {
  const analytics = useAnalytics()
  const location = useLocation()
  const lastSignatureRef = useRef<string | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const search = new URLSearchParams(window.location.search)
    const utm: Record<string, string> = {}
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
      const value = search.get(key)
      if (value) utm[key] = value
    }
    if (Object.keys(utm).length === 0) return

    // Avoid re-identifying on every render — only when UTMs actually change.
    const signature = JSON.stringify(utm)
    if (signature === lastSignatureRef.current) return
    lastSignatureRef.current = signature

    // No userId yet — just trait attach. Pass undefined for userId.
    analytics.identify(undefined, utm)
  }, [analytics, location.search])
}
